/**
 * Native mobile shell (mobile/, Capacitor) integration.
 *
 * The shell loads production menzil.ai in its WebView and injects the
 * Capacitor bridge into the page, so `window.Capacitor.Plugins.*` is
 * callable here WITHOUT bundling any Capacitor npm package into the
 * Next.js app. This module is the only place that touches that global:
 * everything else asks these helpers.
 *
 * Detection is by user agent: the shell appends "MenzilApp/1.0 ios" /
 * "MenzilApp/1.0 android" (capacitor.config.ts appendUserAgent).
 *
 * Push registration mirrors lib/push.ts (web push) in spirit: the page
 * drives the OS permission prompt and POSTs the resulting FCM token to
 * the backend with the normal session cookie, which binds it to the
 * signed-in account. Talks to:
 *   POST   /push/register  <- { token, platform, client_id, locale }
 *   DELETE /push/register  <- { token }
 *   GET    /push/status    -> { registered }
 *
 * Every function is SSR-safe.
 */
import { API_BASE } from "@/lib/api";
import { getClientId } from "@/lib/client-id";

const UA_TOKEN = "MenzilApp/";
// The FCM token this install registered, so unregister can name it and
// re-launches can refresh last_seen_at without a new permission prompt.
const TOKEN_STORAGE_KEY = "menzil_app_push_token";

type PluginListenerHandle = { remove: () => Promise<void> };

interface PushNotificationsPlugin {
  checkPermissions(): Promise<{ receive: string }>;
  requestPermissions(): Promise<{ receive: string }>;
  register(): Promise<void>;
  addListener(
    event: "registration",
    cb: (token: { value: string }) => void,
  ): Promise<PluginListenerHandle>;
  addListener(
    event: "registrationError",
    cb: (err: unknown) => void,
  ): Promise<PluginListenerHandle>;
  addListener(
    event: "pushNotificationActionPerformed",
    cb: (action: { notification: { data?: Record<string, string> } }) => void,
  ): Promise<PluginListenerHandle>;
}

interface AppPlugin {
  addListener(
    event: "appUrlOpen",
    cb: (data: { url: string }) => void,
  ): Promise<PluginListenerHandle>;
  getLaunchUrl(): Promise<{ url?: string } | null>;
}

interface BrowserPlugin {
  open(opts: { url: string }): Promise<void>;
  close(): Promise<void>;
}

interface CapacitorGlobal {
  isNativePlatform?: () => boolean;
  Plugins?: {
    PushNotifications?: PushNotificationsPlugin;
    App?: AppPlugin;
    Browser?: BrowserPlugin;
  };
}

function capacitor(): CapacitorGlobal | null {
  if (typeof window === "undefined") return null;
  const cap = (window as Window & { Capacitor?: CapacitorGlobal }).Capacitor;
  return cap && cap.isNativePlatform?.() ? cap : null;
}

/** Is the page running inside the mobile shell? */
export function isNativeApp(): boolean {
  if (typeof navigator === "undefined") return false;
  return navigator.userAgent.includes(UA_TOKEN);
}

/** "ios" | "android" | null - which shell, per the appended UA token. */
export function nativePlatform(): "ios" | "android" | null {
  if (typeof navigator === "undefined") return null;
  const ua = navigator.userAgent;
  const at = ua.indexOf(UA_TOKEN);
  if (at === -1) return null;
  const rest = ua.slice(at);
  if (rest.includes("android")) return "android";
  if (rest.includes("ios")) return "ios";
  return null;
}

/** Typed access to the injected plugins; null outside the shell. */
export function nativePlugins(): CapacitorGlobal["Plugins"] | null {
  return capacitor()?.Plugins ?? null;
}

function currentLocale(): string {
  if (typeof document === "undefined") return "az";
  const seg = window.location.pathname.split("/")[1];
  return ["az", "ru", "en"].includes(seg) ? seg : "az";
}

/**
 * Opt this install into app push: OS permission prompt (first time),
 * FCM registration, then persist the token on the backend under the
 * current session's account.
 *
 * Returns { ok: true } or { ok: false, reason } with reason one of
 * "unsupported" | "denied" | "error" - same contract style as
 * lib/push.ts subscribeToPush so the alert form maps messages the
 * same way.
 */
export async function registerAppPush(): Promise<{ ok: boolean; reason?: string }> {
  const push = nativePlugins()?.PushNotifications;
  if (!push) return { ok: false, reason: "unsupported" };

  // Server-side gate FIRST (mirrors web push's /alerts/push/key check):
  // calling the native register() in a build whose Firebase config is
  // missing raises a fatal native exception, and server creds +
  // google-services.json are provisioned together - so "server not
  // configured" is the reliable proxy for "do not touch FCM".
  try {
    const cfg = await fetch(`${API_BASE}/push/config`, {
      credentials: "include",
    });
    if (!cfg.ok) return { ok: false, reason: "error" };
    const data = (await cfg.json()) as { enabled?: boolean };
    if (!data.enabled) return { ok: false, reason: "disabled" };
  } catch {
    return { ok: false, reason: "error" };
  }

  try {
    let status = await push.checkPermissions();
    if (status.receive === "prompt" || status.receive === "prompt-with-rationale") {
      status = await push.requestPermissions();
    }
    if (status.receive !== "granted") {
      return { ok: false, reason: "denied" };
    }

    // register() is async-fire: the token arrives on the "registration"
    // listener. Race it against a timeout so a hung FCM handshake can't
    // wedge the form. NOTE: the injected bridge returns listener handles
    // synchronously (npm typings say Promise<handle>); Promise.resolve()
    // tolerates both shapes.
    const token = await new Promise<string | null>((resolve) => {
      let settled = false;
      const settle = (value: string | null) => {
        if (!settled) {
          settled = true;
          resolve(value);
        }
      };
      const timer = setTimeout(() => settle(null), 20_000);
      void Promise.resolve(
        push.addListener("registration", (t) => {
          clearTimeout(timer);
          settle(t.value);
        }),
      )
        .then(() => Promise.resolve(
          push.addListener("registrationError", () => {
            clearTimeout(timer);
            settle(null);
          }),
        ))
        .then(() => push.register())
        .catch(() => {
          clearTimeout(timer);
          settle(null);
        });
    });
    if (!token) return { ok: false, reason: "error" };

    const res = await fetch(`${API_BASE}/push/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        token,
        platform: nativePlatform() ?? "android",
        client_id: getClientId(),
        locale: currentLocale(),
      }),
    });
    if (!res.ok) return { ok: false, reason: "error" };

    try {
      localStorage.setItem(TOKEN_STORAGE_KEY, token);
    } catch {
      /* private mode - unregister will just be a no-op */
    }
    return { ok: true };
  } catch {
    return { ok: false, reason: "error" };
  }
}

/** Tombstone this install's token on the backend. Best-effort. */
export async function unregisterAppPush(): Promise<void> {
  let token: string | null = null;
  try {
    token = localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    return;
  }
  if (!token) return;
  try {
    await fetch(`${API_BASE}/push/register`, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ token }),
    });
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch {
    /* best-effort - a dead token is pruned on its next UNREGISTERED */
  }
}

/** Does the current account have any active app device token
 *  (this install or another)? Server truth for the alert form. */
export async function isAppPushRegistered(): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/push/status`, {
      credentials: "include",
    });
    if (!res.ok) return false;
    const data = (await res.json()) as { registered?: boolean };
    return !!data.registered;
  } catch {
    return false;
  }
}

/** Open a URL in the system browser (SFSafariViewController / Custom
 *  Tabs). Falls back to window.open outside the shell. */
export async function openExternal(url: string): Promise<void> {
  const browser = nativePlugins()?.Browser;
  if (browser) {
    await browser.open({ url });
  } else if (typeof window !== "undefined") {
    window.open(url, "_blank", "noopener");
  }
}

/** Close the system-browser sheet if one is open. No-op elsewhere. */
export async function closeExternal(): Promise<void> {
  try {
    await nativePlugins()?.Browser?.close();
  } catch {
    /* Android Custom Tabs can't be closed programmatically - fine */
  }
}
